import React, { useState, useEffect } from 'react';
import Head from 'next/head';

import axios, { API } from 'config/axios';
import { withTranslation, Router } from 'config/i18n';

import VehiclePageView from 'views/vehicle_page/VehiclePageView';
import isCrawlers from 'common/utils/isCrawlers';
import generateMetaData from 'utils/generateMetaData';
import generateSlugsHrefLangs from 'utils/generateSlugsHrefLangs';
import useHandleResponse from 'utils/useHandleResponse';
import { serverFetch } from 'utils/serverFetch';
import { MuiPickersUtilsProvider } from '@material-ui/pickers';
import { useLayoutState } from 'components/shared/Layout';
import Error from 'components/shared/Error';

// pick a date util library
import DayJsUtils from '@date-io/dayjs';
import { getDeviceType } from 'common/utils/getDeviceType';

const BikePage = (props) => {
  const {
    serverData, headLinks, ssrRender, reivewsSSRData, errorStatusCode,
  } = props;
  const [pageData, setPageData] = useState(serverData);
  const { handleError } = useHandleResponse();
  const { updateLayoutState } = useLayoutState();

  if (errorStatusCode) {
    return <Error statusCode={errorStatusCode || 500} />;
  }
  const renderSSRCanonicalLinks = () => (
    <>
      <link rel="canonical" href={headLinks.canonical} />
      {headLinks.urls.map((local) => (
        <link key={local.hreflang} rel="alternate" href={local.url} hrefLang={local.hreflang} />
      ))}
    </>
  );

  useEffect(() => {
    if (typeof window !== 'undefined' && !ssrRender) {
      const params = { ...Router.query };
      delete params.id;
      // Router.query.id will get undefined for now but after integration
      axios
        .get(API.vehicles.vehicle.replace('{id}', Router.query.id), {
          params,
        })
        .then((res) => {
          setPageData(res.data);
          const hreflangData = generateSlugsHrefLangs(
            Router.asPath,
            {
              make: res.data.vehicle.make,
              model: res.data.vehicle.model,
              geo: res.data.location.address.geo,
            },
            `/${Router.query.id}`,
          );
          updateLayoutState((prev) => ({
            ...prev,
            hreflangData,
          }));
        })
        .catch((err) => {
          let result;
          switch (err.response.status) {
            case 301:
              result = err.response.data;
              Router.push(
                `/${result.make_slug}/${result.model_slug}/${result.geo_slug}/${Router.query.id}`,
              );
              break;
            default:
              handleError(err, false);
          }
        });
    }
  }, [!ssrRender && Router?.query]);

  return (
    <>
      <Head>
        {pageData?.meta_data && generateMetaData(pageData.meta_data)}
        {typeof window === 'undefined' && renderSSRCanonicalLinks()}
      </Head>
      <MuiPickersUtilsProvider utils={DayJsUtils}>
        {pageData?.vehicle && (
          <>
            <VehiclePageView
              ssrRender={ssrRender}
              pageData={pageData}
              reivewsSSRData={reivewsSSRData}
            />
            <script
              type="application/ld+json"
              dangerouslySetInnerHTML={{
                __html: JSON.stringify(pageData?.structured_data),
              }}
            />
          </>
        )}
      </MuiPickersUtilsProvider>
    </>
  );
};

BikePage.getInitialProps = async (ctx) => {
  const { res, req } = ctx;
  const ssrRender = req ? isCrawlers(req.headers['user-agent']) : false;
  let serverData = {};
  // generate canonical and hrefLang
  let headLinks = {};

  const params = { ...ctx.query };
  delete params.id;
  let reivewsSSRData = null;
  let errorStatusCode = null;
  if (ssrRender) {
    const { localeSubpaths } = require('next/config').default().publicRuntimeConfig;
    const [vehiclePageRes, reviewRes] = await Promise.all([
      serverFetch(ctx, API.vehicles.vehicle.replace('{id}', ctx.query.id), params),
      serverFetch(ctx, API.vehicles.reviews.replace('{id}', ctx.query.id), params),
    ]);
    if (vehiclePageRes) {
      const { success, data, error } = vehiclePageRes;
      if (success && data) {
        serverData = data;
        headLinks = generateSlugsHrefLangs(
          ctx.req.url,
          {
            make: data.vehicle.make,
            model: data.vehicle.model,
            geo: data.location.address.geo,
          },
          `/${ctx.query.id}`,
        );
      } else {
        switch (error?.response && error.response.status) {
          case 301:
            if (res) {
              // server
              const result = error.response.data;
              const subPath = localeSubpaths[req.language]
                ? `/${localeSubpaths[req.language]}`
                : '';
              // eslint-disable-next-line max-len
              const redirectURL = `${subPath}/${result.make_slug}/${result.model_slug}/${result.geo_slug}/${ctx.query.id}`;
              res.writeHead(301, {
                Location: redirectURL,
              });
              res.end();
            }
            break;
          case 403:
          case 404:
            errorStatusCode = error.response.status;
            res.statusCode = error.response.status;
            break;
          default:
            res.statusCode = 502;
            res.end();
            break;
        }
      }
    }
    if (reviewRes.success) {
      const { data, error } = reviewRes;
      if (data) {
        reivewsSSRData = data;
      }
      if (error) {
        reivewsSSRData = [];
      }
    }
  }

  let deviceType = '';
  deviceType = getDeviceType(ctx.req?.headers['user-agent']);

  return {
    namespacesRequired: [
      'fe_bike_features_measurments',
      'fe_insurance_via_owner_component',
      'fe_commercial_host_note_component',
      'fe_es_vehicle_page',
      'fe_share_component',
      'fe_es_expandable_description_component',
      'fe_es_user_info_sub_info_component',
      'fe_es_hosted_by_component',
      'fe_es_technical_details_component',
      'fe_bike_owner_widget',
      'fe_es_reviews_title',
      'pagination',
    ],
    serverData,
    headLinks,
    ssrRender,
    reivewsSSRData,
    deviceType,
    errorStatusCode,
  };
};

export default withTranslation('fe_es_vehicle_page')(BikePage);
